import { useState, type ReactNode } from 'react';
import { ArrowsLeftRightIcon, type Icon as PhosphorIcon } from '@phosphor-icons/react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@voll-intelligence/ui-kit/ui/tabs';

/*
 * Os controles da rota das barras de busca da conversa — o do aéreo e o do
 * ônibus: as abas da forma da viagem e o inversor ORIGEM ⇄ DESTINO. Saíram do
 * `planner_form` do genui-kit para o ônibus usar os mesmos. `genui2` na raiz
 * de cada um: o card do aéreo não vive sob a raiz de tema do DS (a mesma razão
 * dos botões da saia do calendário).
 */

/** Existe desde a abertura, com as duas pontas vazias (#4636): trocar um lado
 *  vazio só move o valor de campo, então o botão nunca é alvo morto.
 *
 *  Cada clique soma meia volta ao ícone — acumula, em vez de alternar 0/180, pra
 *  cliques seguidos girarem sempre no mesmo sentido. Some no estreito: a
 *  dobradiça só existe enquanto as duas pontas estão lado a lado. */
export function SwapButton({
  label,
  disabled,
  onSwap,
}: {
  readonly label: string;
  readonly disabled: boolean;
  readonly onSwap: () => void;
}): ReactNode {
  const [turns, setTurns] = useState(0);
  return (
    <Button
      type="button"
      variant="outline"
      size="icon"
      className="genui2 -mx-1 mb-0.5 shrink-0 self-end rounded-full max-sm:hidden"
      aria-label={label}
      disabled={disabled}
      onClick={() => {
        setTurns((n) => n + 1);
        onSwap();
      }}
    >
      <ArrowsLeftRightIcon
        size={14}
        aria-hidden
        className="size-3.5 transition-transform motion-reduce:transition-none"
        style={{ transform: `rotate(${String(turns * 180)}deg)` }}
      />
    </Button>
  );
}

export type TripTypeTab<K extends string> = {
  readonly key: K;
  readonly label: string;
  readonly Icon: PhosphorIcon;
};

/** As abas da forma da viagem ("Ida e volta", "Somente ida", …). O glifo só
 *  aparece na aba ligada: as setas formam PAR — duas direções contra uma —, e o
 *  par diz a forma sem legenda. Largura cheia e alvos de peso igual: nenhuma
 *  forma é mais "certa" que as outras. */
export function TripTypeTabs<K extends string>({
  tabs,
  active,
  disabled,
  onSelect,
}: {
  readonly tabs: readonly TripTypeTab<K>[];
  readonly active: K;
  readonly disabled: boolean;
  readonly onSelect: (key: K) => void;
}): ReactNode {
  return (
    <Tabs
      className="genui2 bg-transparent"
      value={active}
      onValueChange={(value) => {
        // O valor do Base UI é `string | number`; só o que nasceu da lista volta.
        const picked = tabs.find((tab) => tab.key === value);
        if (picked) onSelect(picked.key);
      }}
    >
      <TabsList variant="pill" className="w-full">
        {tabs.map((tab) => (
          /* `text-[13px]`: a métrica do `SegmentedControl / sm` do frame. `min-w-0`
             + reticências: o gatilho do DS é `flex-1 whitespace-nowrap`, e sem o
             mínimo zerado o item não encolhe — no card a 375px "Várias cidades"
             com o glifo estourava a trilha. `max-sm:px-1!`: no mesmo degrau a aba
             não cabe o `px-3` da variante pill; o `!` é porque o preset o declara
             por `group-data-[variant=pill]`, que vence utilitária simples. */
          <TabsTrigger
            key={tab.key}
            value={tab.key}
            disabled={disabled}
            className="min-w-0 overflow-hidden text-[13px] text-ellipsis max-sm:px-1!"
          >
            {/* Monta e desmonta com a seleção, e é isso que faz a animação rodar de
                novo a cada troca. No estreito o glifo sai antes do rótulo. */}
            {tab.key === active && (
              <span
                aria-hidden="true"
                className="inline-flex animate-in duration-200 fade-in slide-in-from-bottom-1.5 motion-reduce:animate-none max-sm:hidden"
              >
                {/* `size-3.5`: o gatilho força `size-4` em svg sem classe de tamanho. */}
                <tab.Icon size={14} className="size-3.5" />
              </span>
            )}
            <span className="truncate">{tab.label}</span>
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}
